import { RefObject, useContext, useMemo, useRef } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useResizeObserver } from 'usehooks-ts';

import { SchemableFormNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { useWebEDIStore } from 'stores/webedi/webedi';

import {
  layoutColumns,
  measureTextWithCanvas,
  resolveColumnWidths,
  rightAddonWidthsKey,
  toIconColumnPaths,
} from '../layoutHelpers/columnWidths';
import { getItemsCountAtPath } from './useEmbeddedTableRows/helpers';

/**
 * Widths are resolved only when the table shape changes, so typing, scrolling and virtualization never move a column.
 * The data-driven exceptions are the collapse toggle, a `rightAddon`, and an icon in a view cell (an error, a warning
 * or an AI edit) appearing or disappearing.
 * The scroll container's width comes from the page, never from the table, so observing it cannot feed back.
 */
export const useColumnLayout = <TData, TValue>(
  columns: Array<ColumnDef<TData, TValue>>,
  data: Array<TData>,
  reshapeKey: number,
  parentColumnPaths: Array<string>,
  {
    isEditable,
    baseData,
    revisedData,
    dataPrefix,
  }: { isEditable: boolean; baseData?: Array<TData>; revisedData?: Array<TData>; dataPrefix?: string },
  scrollContainerRef: RefObject<HTMLDivElement | null>,
) => {
  const dataRef = useRef(data);
  dataRef.current = data;
  const hasData = data.length > 0;
  const hasCollapseToggle = useMemo(
    () => data.some((row) => parentColumnPaths.some((path) => getItemsCountAtPath(row, path) > 1)),
    [data, parentColumnPaths],
  );
  const iconColumnPaths = useIconColumnPaths(dataPrefix);
  const addonKey = useMemo(() => rightAddonWidthsKey(columns, data, measureTextWithCanvas), [columns, data]);
  const widths = useMemo(
    () =>
      resolveColumnWidths({
        columns,
        // In diff mode the cells show the revised values, which the form rows may not hold yet.
        data: revisedData ?? dataRef.current,
        hasCollapseToggle,
        isEditable,
        baseData,
        iconColumnPaths,
        measureText: measureTextWithCanvas,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, reshapeKey, hasData, hasCollapseToggle, isEditable, baseData, revisedData, addonKey, iconColumnPaths],
  );
  const { width: containerWidth = 0 } = useResizeObserver({
    ref: scrollContainerRef as RefObject<HTMLDivElement>,
    box: 'content-box',
  });
  return useMemo(() => layoutColumns(widths, containerWidth), [widths, containerWidth]);
};

/** The columns with a view cell that draws an icon beside its value. Edit cells always keep the icon slot. */
const useIconColumnPaths = (dataPrefix: string | undefined) => {
  // A table outside a navigation provider shows no annotation icons.
  const useIconFieldPathsKey =
    useContext(SchemableFormNavigationContext)?.service.useIconFieldPathsKey ?? noIconFieldPaths;
  const annotatedFieldPathsKey = useIconFieldPathsKey(dataPrefix ? `${dataPrefix}.` : '');
  const aiEditedFieldPaths = useWebEDIStore((state) => state.fieldPathsWithAIEditions);
  return useMemo(
    () =>
      dataPrefix
        ? toIconColumnPaths([...annotatedFieldPathsKey.split('\n'), ...Array.from(aiEditedFieldPaths)], dataPrefix)
        : NO_ICON_COLUMN_PATHS,
    [annotatedFieldPathsKey, aiEditedFieldPaths, dataPrefix],
  );
};

const noIconFieldPaths = (_prefix: string) => '';
const NO_ICON_COLUMN_PATHS: ReadonlySet<string> = new Set();
